import { useMutation, useQuery } from '@tanstack/react-query'
import { BlogPostsFilterParams, CreateBlogPostParams, UpdateBlogPostParams } from '@/types/blog-post'
import { useBlogPostsStore } from '@/store'
import { useBulkDeleteSuccessHandler } from './use-bulk-delete'

export const useBlogPosts = (params: BlogPostsFilterParams) => {
  const fetchBlogPosts = useBlogPostsStore(state => state.fetchBlogPosts)

  return useQuery({
    queryKey: ['blog-posts', params],
    queryFn: () => fetchBlogPosts(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  })
}

export const useBlogPost = (id: string) => {
  const getBlogPost = useBlogPostsStore(state => state.getBlogPost)

  return useQuery({
    queryKey: ['blog-post', id],
    queryFn: () => getBlogPost(id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!id
  })
}

export const useCreateBlogPost = () => {
  const createBlogPost = useBlogPostsStore(state => state.createBlogPost)

  return useMutation({
    mutationFn: (params: CreateBlogPostParams) => createBlogPost(params),
    onSuccess: () => {}
  })
}

export const useUpdateBlogPost = () => {
  const updateBlogPost = useBlogPostsStore(state => state.updateBlogPost)

  return useMutation({
    mutationFn: ({ id, params }: { id: string; params: UpdateBlogPostParams }) => updateBlogPost(id, params),
    onSuccess: () => {}
  })
}

export const useDeleteBlogPost = () => {
  const deleteBlogPost = useBlogPostsStore(state => state.deleteBlogPost)

  return useMutation({
    mutationFn: (id: string) => deleteBlogPost(id),
    onSuccess: () => {}
  })
}

export const useBulkDeleteBlogPosts = () => {
  const bulkDeleteBlogPosts = useBlogPostsStore(state => state.bulkDeleteBlogPosts)
  const deselectAllBlogPosts = useBlogPostsStore(state => state.deselectAllBlogPosts)
  const handleBulkDeleteSuccess = useBulkDeleteSuccessHandler()

  return useMutation({
    mutationFn: (ids: string[]) => bulkDeleteBlogPosts(ids),
    onSuccess: response => {
      deselectAllBlogPosts()
      handleBulkDeleteSuccess(response)
    }
  })
}
